import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  IconButton,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { storage } from '../../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../contexts/AuthContext';
import { getirApi, mainApi, hepsijetApi, mngCargoApi, n11Api, hepsiburadaApi, pazaramaApi, pttavmApi, trendyolMpApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  FoodPlatform,
  CargoPlatform,
  PlatformIntegration,
  BrandReceiptSettings,
  FOOD_PLATFORM_KEYS,
  PLATFORM_LABELS,
  STORE_NAME_MAX_LENGTH,
} from '../../../types/platformIntegration';
import BrandReceiptHeaderForm from '../../integrations/BrandReceiptHeaderForm';

// Yemek platformları listesi
const FOOD_PLATFORMS: FoodPlatform[] = FOOD_PLATFORM_KEYS;

// Kargo platformları listesi
const CARGO_PLATFORMS: CargoPlatform[] = ['hepsijet', 'mng'];

// Platform renkleri (etiketler ortak PLATFORM_LABELS'tan gelir)
const PLATFORM_COLORS: Record<Platform, string> = {
  trendyol: '#f27a1a',
  n11: '#7b1fa2',
  hepsiburada: '#ff6000',
  pazarama: '#e6007e',
  pttavm: '#e30613',
  amazon: '#ff9900',
  yemeksepeti: '#e31e24',
  getir: '#5d3ebc',
  migros_yemek: '#ff6600',
  trendyol_yemek: '#06c167',
  hepsijet: '#ff6000',
  mng: '#FFCC00',
};

// Platform adını al
const getPlatformName = (platform: Platform): string => {
  return PLATFORM_LABELS[platform] || platform;
};

// Platform rengini al
const getPlatformColor = (platform: Platform): string => {
  return PLATFORM_COLORS[platform] || '#1976d2';
};

interface EditIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  integration: PlatformIntegration;
  onSaved: () => void;
}

const EditIntegrationModal: React.FC<EditIntegrationModalProps> = ({
  open,
  onClose,
  companyId,
  integration,
  onSaved
}) => {
  const { t, i18n } = useTranslation('salesSystem');
  const currentLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const { branches } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectedOverride, setConnectedOverride] = useState<boolean | null>(null);
  const [showApiSecret, setShowApiSecret] = useState(false);
  
  // Getir POS Status state'leri
  const [getirPosStatus, setGetirPosStatus] = useState<'loading' | 'open' | 'closed' | 'error' | null>(null);
  const [getirPosLoading, setGetirPosLoading] = useState(false);
  const [getirPosError, setGetirPosError] = useState<string | null>(null);
  
  // BranchId'den şube adını bul
  const getBranchName = (branchId: string | null | undefined): string | null => {
    if (!branchId) return null;
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || branchId;
  };
  
  // Snackbar State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Form verileri - hem yeni API hem legacy format'ı destekle
  const getInitialFormData = () => ({
    enabled: integration.enabled ?? integration.isEnabled ?? true,
    branchId: integration.branchId ?? '',
    storeName: integration.storeName ?? '',
    credentials: {
      apiKey: integration.apiKey ?? integration.credentials?.apiKey ?? '',
      apiSecret: integration.apiSecret ?? integration.credentials?.apiSecret ?? '',
      sellerId: integration.sellerId ?? integration.credentials?.sellerId ?? '',
      restaurantId: integration.restaurantId ?? integration.credentials?.restaurantId ?? '',
      appSecretKey: integration.appSecretKey ?? integration.credentials?.appSecretKey ?? '',
      restaurantSecretKey: integration.restaurantSecretKey ?? integration.credentials?.restaurantSecretKey ?? ''
    }
  });

  const [formData, setFormData] = useState(getInitialFormData());

  // Markaya özel fiş başlığı. null → ekran ayarındaki başlık kullanılır.
  const parseReceiptSettings = (json?: string | null): BrandReceiptSettings | null => {
    if (!json || !json.trim()) return null;
    try {
      const parsed = JSON.parse(json);
      return parsed && typeof parsed === 'object' ? (parsed as BrandReceiptSettings) : null;
    } catch {
      console.warn('Brand receipt settings JSON could not be parsed, falling back to screen default.');
      return null;
    }
  };

  const [brandReceipt, setBrandReceipt] = useState<BrandReceiptSettings | null>(
    parseReceiptSettings(integration.receiptSettingsJson)
  );
  const [receiptSectionOpen, setReceiptSectionOpen] = useState(false);

  // HepsiJet merchant config (firma-özel sabit değerler). Edit'te mevcut config çekilir.
  const emptyHepsijetMerchant = {
    companyName: '',
    abbreviationCode: '',
    companyAddressId: '',
    senderCountry: 'Türkiye',
    senderCity: '',
    senderTown: '',
    senderDistrict: '',
    senderAddressLine1: '',
    currentXDockCode: ''
  };
  const [hepsijetMerchant, setHepsijetMerchant] = useState({ ...emptyHepsijetMerchant });

  const emptyMngMerchant = {
    senderName: '',
    senderPhone: '',
    senderCountry: 'Türkiye',
    senderCity: '',
    senderDistrict: '',
    senderNeighborhood: '',
    senderAddressLine: '',
    isTestMode: true
  };
  const [mngMerchant, setMngMerchant] = useState({ ...emptyMngMerchant });

  // Marka fişi logosu quickSale ekran logosuyla aynı yere yüklenir.
  const uploadBrandLogo = async (file: File): Promise<string> => {
    if (!companyId) {
      throw new Error(t('editIntegrationModal.errors.companyInfoNotFound'));
    }
    const logoRef = storageRef(
      storage,
      `companies/${companyId}/brand-logos/integration-${integration.id}-${Date.now()}`
    );
    const uploadResult = await uploadBytes(logoRef, file);
    return await getDownloadURL(uploadResult.ref);
  };
  const emptyMngPlatform = {
    clientId: '',
    clientSecret: '',
    apiBaseUrl: 'https://testapi.mngkargo.com.tr',
    hasClientSecret: false
  };
  const [mngPlatform, setMngPlatform] = useState({ ...emptyMngPlatform });
  const [showMngClientSecret, setShowMngClientSecret] = useState(false);

  const handleMerchantChange = (field: string, value: string) => {
    setHepsijetMerchant(prev => ({ ...prev, [field]: value }));
  };

  const handleMngMerchantChange = (field: string, value: string | boolean) => {
    setMngMerchant(prev => ({ ...prev, [field]: value }));
  };

  const handleMngPlatformChange = (field: string, value: string) => {
    setMngPlatform(prev => ({ ...prev, [field]: value }));
  };

  // Integration değiştiğinde form verilerini güncelle
  useEffect(() => {
    setFormData(getInitialFormData());
    const parsed = parseReceiptSettings(integration.receiptSettingsJson);
    setBrandReceipt(parsed);
    setReceiptSectionOpen(Boolean(parsed));
  }, [integration]);

  // HepsiJet entegrasyonu açıldığında mevcut merchant config'i çek (varsa doldur).
  useEffect(() => {
    if (!open || integration.platform !== 'hepsijet') {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await hepsijetApi.get('/api/hepsijet/merchant-config');
        const d = res.data || {};
        if (cancelled) return;
        setHepsijetMerchant({
          companyName: d.companyName ?? d.CompanyName ?? '',
          abbreviationCode: d.abbreviationCode ?? d.AbbreviationCode ?? '',
          companyAddressId: d.companyAddressId ?? d.CompanyAddressId ?? '',
          senderCountry: d.senderCountry ?? d.SenderCountry ?? 'Türkiye',
          senderCity: d.senderCity ?? d.SenderCity ?? '',
          senderTown: d.senderTown ?? d.SenderTown ?? '',
          senderDistrict: d.senderDistrict ?? d.SenderDistrict ?? '',
          senderAddressLine1: d.senderAddressLine1 ?? d.SenderAddressLine1 ?? '',
          currentXDockCode: d.currentXDockCode ?? d.CurrentXDockCode ?? ''
        });
      } catch {
        // 404 = henüz config yok; boş bırak.
        if (!cancelled) setHepsijetMerchant({ ...emptyHepsijetMerchant });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integration.platform]);

  // MNG entegrasyonu acildiginda mevcut merchant config'i cek (varsa doldur).
  useEffect(() => {
    if (!open || integration.platform !== 'mng') {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await mngCargoApi.get('/api/MngCargo/merchant-config');
        const d = res.data?.Data ?? res.data?.data ?? res.data ?? {};
        if (cancelled) return;
        setMngMerchant({
          senderName: d.senderName ?? d.SenderName ?? '',
          senderPhone: d.senderPhone ?? d.SenderPhone ?? '',
          senderCountry: d.senderCountry ?? d.SenderCountry ?? 'Türkiye',
          senderCity: d.senderCity ?? d.SenderCity ?? '',
          senderDistrict: d.senderDistrict ?? d.SenderDistrict ?? '',
          senderNeighborhood: d.senderNeighborhood ?? d.SenderNeighborhood ?? '',
          senderAddressLine: d.senderAddressLine ?? d.SenderAddressLine ?? '',
          isTestMode: d.isTestMode ?? d.IsTestMode ?? true
        });
      } catch {
        if (!cancelled) setMngMerchant({ ...emptyMngMerchant });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integration.platform]);

  // MNG Apizone platform credential (ortam degisince yeniden yukle).
  useEffect(() => {
    if (!open || integration.platform !== 'mng') {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await mngCargoApi.get('/api/MngCargo/platform-config', {
          params: { isTestMode: mngMerchant.isTestMode }
        });
        const d = res.data?.Data ?? res.data?.data ?? {};
        if (cancelled) return;
        setMngPlatform({
          clientId: d.clientId ?? d.ClientId ?? '',
          clientSecret: '',
          apiBaseUrl: d.apiBaseUrl ?? d.ApiBaseUrl
            ?? (mngMerchant.isTestMode ? 'https://testapi.mngkargo.com.tr' : 'https://api.mngkargo.com.tr'),
          hasClientSecret: !!(d.hasClientSecret ?? d.HasClientSecret)
        });
      } catch {
        if (!cancelled) {
          setMngPlatform({
            ...emptyMngPlatform,
            apiBaseUrl: mngMerchant.isTestMode
              ? 'https://testapi.mngkargo.com.tr'
              : 'https://api.mngkargo.com.tr'
          });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integration.platform, mngMerchant.isTestMode]);

  // Getir entegrasyonu için POS durumunu sorgula (modal açıldığında)
  useEffect(() => {
    if (open && integration.platform === 'getir' && companyId) {
      checkGetirPosStatus();
    }
    if (!open) {
      setGetirPosStatus(null);
      setGetirPosError(null);
    }
  }, [open, integration.platform, companyId]);

  const checkGetirPosStatus = async () => {
    if (!companyId) return;
    
    setGetirPosStatus('loading');
    setGetirPosError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('companyId', companyId);
      if (integration.branchId) params.append('branchId', integration.branchId);

      console.log(`🔌 [Getir POS] POS entegrasyon durumu sorgulanıyor`);
      
      const response = await getirApi.get(`/api/Restaurant/pos-status?${params.toString()}`);
      const result = response.data;
      
      if (result.success) {
        const isActive = result.posStatus === 100;
        setGetirPosStatus(isActive ? 'open' : 'closed');
        console.log(`🔌 [Getir POS] POS Durumu: ${isActive ? 'AÇIK (100)' : 'KAPALI (200)'} (RestaurantId: ${result.restaurantId})${result.note ? ` - ${result.note}` : ''}`);
        if (result.note) {
          console.log(`ℹ️ [Getir POS] Not: ${result.note}`);
        }
      } else {
        setGetirPosStatus('error');
        setGetirPosError(result.error || t('editIntegrationModal.errors.posStatusFetchFailed'));
      }
    } catch (err: any) {
      console.error('❌ [Getir POS] POS durum sorgulama hatası:', err);
      setGetirPosStatus('error');
      setGetirPosError(err.message || t('editIntegrationModal.errors.connectionError'));
    }
  };

  const handleGetirPosToggle = async (action: 'open' | 'close') => {
    if (!companyId) return;
    
    setGetirPosLoading(true);
    setGetirPosError(null);
    
    try {
      const posStatusValue = action === 'open' ? 100 : 200;
      
      const params = new URLSearchParams();
      params.append('companyId', companyId);
      if (integration.branchId) params.append('branchId', integration.branchId);
      params.append('posStatus', posStatusValue.toString());

      console.log(`🔄 [Getir POS] POS entegrasyonu ${action === 'open' ? 'açılıyor (100)' : 'kapatılıyor (200)'}...`);
      
      const response = await getirApi.post(`/api/Restaurant/pos-status/update?${params.toString()}`);
      const result = response.data;

      if (result.success) {
        setGetirPosStatus(action === 'open' ? 'open' : 'closed');
        setSnackbar({
          open: true,
          message: action === 'open' ? t('editIntegrationModal.messages.getirPosOpened') : t('editIntegrationModal.messages.getirPosClosed'),
          severity: 'success'
        });
        console.log(`✅ [Getir POS] POS entegrasyonu ${action === 'open' ? 'AÇILDI (100)' : 'KAPATILDI (200)'}`);
      } else {
        setGetirPosError(result.message || (action === 'open' ? t('editIntegrationModal.errors.posOpenFailed') : t('editIntegrationModal.errors.posCloseFailed')));
        setSnackbar({
          open: true,
          message: result.message || (action === 'open' ? t('editIntegrationModal.errors.posOpenFailed') : t('editIntegrationModal.errors.posCloseFailed')),
          severity: 'error'
        });
      }
    } catch (err: any) {
      console.error(`❌ [Getir POS] POS ${action} hatası:`, err);
      setGetirPosError(err.message || t('editIntegrationModal.errors.connectionError'));
      setSnackbar({
        open: true,
        message: t('editIntegrationModal.errors.getirApiConnection', { message: err.message }),
        severity: 'error'
      });
    } finally {
      setGetirPosLoading(false);
    }
  };

  const getPlatformInfo = (platform: Platform) => ({
    label: getPlatformName(platform),
    color: getPlatformColor(platform),
  });

  const platformInfo = getPlatformInfo(integration.platform);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'credentials' 
        ? { ...(prev[field as keyof typeof prev] as any), ...value }
        : value
    }));
  };

  const toggleShowApiSecret = () => {
    setShowApiSecret(prev => !prev);
  };

  // Yemek platformu kontrolü
  const isFoodPlatform = FOOD_PLATFORMS.includes(integration.platform as FoodPlatform);
  // Kargo platformu kontrolü (HepsiJet)
  const isCargoPlatform = CARGO_PLATFORMS.includes(integration.platform as CargoPlatform);

  const validateForm = (): boolean => {
    const isHepsiburada = integration.platform === 'hepsiburada';

    // Kargo platformları: credential + merchant config zorunlu
    if (isCargoPlatform) {
      if (!formData.credentials.apiKey.trim()) {
        setError(integration.platform === 'mng'
          ? t('addIntegrationModal.errors.mngCustomerNumberRequired')
          : t('addIntegrationModal.errors.usernameRequired'));
        return false;
      }
      if (!formData.credentials.apiSecret.trim()) {
        setError(t('addIntegrationModal.errors.passwordRequired'));
        return false;
      }

      if (integration.platform === 'hepsijet') {
        const m = hepsijetMerchant;
        const requiredMerchant: Array<[string, string]> = [
          [m.companyName, t('addIntegrationModal.form.merchantCompanyName')],
          [m.abbreviationCode, t('addIntegrationModal.form.merchantAbbreviationCode')],
          [m.companyAddressId, t('addIntegrationModal.form.merchantCompanyAddressId')],
          [m.senderCity, t('addIntegrationModal.form.merchantSenderCity')],
          [m.senderTown, t('addIntegrationModal.form.merchantSenderTown')],
          [m.senderAddressLine1, t('addIntegrationModal.form.merchantSenderAddress')],
          [m.currentXDockCode, t('addIntegrationModal.form.merchantXDockCode')]
        ];
        const missing = requiredMerchant.find(([val]) => !val?.trim());
        if (missing) {
          setError(t('addIntegrationModal.errors.merchantFieldRequired', { field: missing[1] }));
          return false;
        }
      } else if (integration.platform === 'mng') {
        if (!mngPlatform.clientId?.trim()) {
          setError(t('addIntegrationModal.errors.mngClientIdRequired'));
          return false;
        }
        if (!mngPlatform.clientSecret?.trim() && !mngPlatform.hasClientSecret) {
          setError(t('addIntegrationModal.errors.mngClientSecretRequired'));
          return false;
        }
        const m = mngMerchant;
        const requiredMerchant: Array<[string, string]> = [
          [m.senderName, t('addIntegrationModal.form.mngSenderName')],
          [m.senderPhone, t('addIntegrationModal.form.mngSenderPhone')],
          [m.senderCity, t('addIntegrationModal.form.mngSenderCity')],
          [m.senderDistrict, t('addIntegrationModal.form.mngSenderDistrict')],
          [m.senderAddressLine, t('addIntegrationModal.form.mngSenderAddress')]
        ];
        const missing = requiredMerchant.find(([val]) => !val?.trim());
        if (missing) {
          setError(t('addIntegrationModal.errors.merchantFieldRequired', { field: missing[1] }));
          return false;
        }
      }
      return true;
    }

    // Yemek platformları için özel validasyon
    if (isFoodPlatform) {
      const isMigros = integration.platform === 'migros_yemek';
      const isYemeksepeti = integration.platform === 'yemeksepeti';
      const isTrendyolYemek = integration.platform === 'trendyol_yemek';
      if (!formData.storeName.trim()) {
        setError(t('addIntegrationModal.errors.storeNameRequired'));
        return false;
      }
      if (formData.storeName.trim().length > STORE_NAME_MAX_LENGTH) {
        setError(t('addIntegrationModal.errors.storeNameTooLong', { max: STORE_NAME_MAX_LENGTH }));
        return false;
      }
      if (isTrendyolYemek) {
        if (!formData.credentials.sellerId.trim()) { setError(t('editIntegrationModal.errors.supplierIdRequired')); return false; }
        if (!formData.credentials.restaurantId.trim()) { setError(t('editIntegrationModal.errors.storeIdRequired')); return false; }
        if (!formData.credentials.apiKey.trim()) { setError(t('editIntegrationModal.errors.apiKeyRequired')); return false; }
        if (!formData.credentials.apiSecret.trim()) { setError(t('editIntegrationModal.errors.apiSecretRequired')); return false; }
        return true;
      }
      if (!formData.credentials.restaurantId.trim()) {
        setError(isMigros ? t('editIntegrationModal.errors.restaurantStoreIdRequired') : isYemeksepeti ? t('editIntegrationModal.errors.remoteIdRequired') : t('editIntegrationModal.errors.restaurantIdRequired'));
        return false;
      }
      if (!formData.credentials.appSecretKey.trim()) {
        setError(isMigros ? t('editIntegrationModal.errors.xApiKeyRequired') : isYemeksepeti ? t('editIntegrationModal.errors.chainCodeRequired') : t('editIntegrationModal.errors.appSecretRequired'));
        return false;
      }
      if (!isYemeksepeti && !formData.credentials.restaurantSecretKey.trim()) {
        setError(isMigros ? t('editIntegrationModal.errors.chainIdRequired') : t('editIntegrationModal.errors.restaurantSecretRequired'));
        return false;
      }
      return true;
    }
    
    // Pazaryeri platformları için validasyon
    if (!isHepsiburada && !formData.credentials.apiKey.trim()) {
      setError(t('editIntegrationModal.errors.apiKeyRequired'));
      return false;
    }
    
    if (!formData.credentials.apiSecret.trim()) {
      setError(t('editIntegrationModal.errors.apiSecretRequired'));
      return false;
    }
    
    if (['trendyol', 'hepsiburada', 'amazon', 'pazarama'].includes(integration.platform) && !formData.credentials.sellerId.trim()) {
      setError(t('editIntegrationModal.errors.sellerIdRequired'));
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!companyId) {
      setError(t('editIntegrationModal.errors.companyInfoNotFound'));
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // storeName/receiptSettingsJson yalnızca yemek platformlarında gönderilir.
      // Backend'de null "mevcut değeri koru", boş string "varsayılana dön" demektir.
      const isFood = FOOD_PLATFORMS.includes(integration.platform as FoodPlatform);

      const requestBody = {
        id: integration.id,
        isEnabled: formData.enabled,
        branchId: formData.branchId || null,
        storeName: isFood ? formData.storeName.trim() : null,
        receiptSettingsJson: isFood ? (brandReceipt ? JSON.stringify(brandReceipt) : '') : null,
        apiKey: integration.platform === 'hepsiburada'
          ? null
          : (formData.credentials.apiKey || null),
        apiSecret: formData.credentials.apiSecret || null,
        sellerId: formData.credentials.sellerId || null,
        restaurantId: formData.credentials.restaurantId || null,
        appSecretKey: formData.credentials.appSecretKey || null,
        restaurantSecretKey: formData.credentials.restaurantSecretKey || null
      };
      
      console.log(`🔄 [${integration.platform.toUpperCase()} UPDATE] .NET API'de güncelleniyor...`, requestBody);
      
      const response = await mainApi.post('/api/platformintegration/update', requestBody);
      const result = response.data;

      if (!result.success) {
        throw new Error(result.message || t('editIntegrationModal.errors.updateFailed'));
      }

      // Kargo: firma-ozel merchant config'i guncelle/ekle.
      if (integration.platform === 'hepsijet') {
        await hepsijetApi.post('/api/hepsijet/merchant-config', {
          companyName: hepsijetMerchant.companyName.trim(),
          abbreviationCode: hepsijetMerchant.abbreviationCode.trim(),
          companyAddressId: hepsijetMerchant.companyAddressId.trim(),
          senderCountry: hepsijetMerchant.senderCountry?.trim() || 'Türkiye',
          senderCity: hepsijetMerchant.senderCity.trim(),
          senderTown: hepsijetMerchant.senderTown.trim(),
          senderDistrict: hepsijetMerchant.senderDistrict?.trim() || null,
          senderAddressLine1: hepsijetMerchant.senderAddressLine1.trim(),
          currentXDockCode: hepsijetMerchant.currentXDockCode.trim()
        });
      } else if (integration.platform === 'mng') {
        const platformPayload: Record<string, unknown> = {
          ClientId: mngPlatform.clientId.trim(),
          IsTestMode: mngMerchant.isTestMode
        };
        if (mngPlatform.clientSecret?.trim()) {
          platformPayload.ClientSecret = mngPlatform.clientSecret.trim();
        }
        const platformRes = await mngCargoApi.post('/api/MngCargo/platform-config', platformPayload);
        const platformOk = platformRes.data?.Success ?? platformRes.data?.success;
        if (!platformOk) {
          throw new Error(
            platformRes.data?.Message || platformRes.data?.message
            || t('addIntegrationModal.errors.mngPlatformSaveFailed')
          );
        }

        await mngCargoApi.post('/api/MngCargo/merchant-config', {
          SenderName: mngMerchant.senderName.trim(),
          SenderPhone: mngMerchant.senderPhone.trim(),
          SenderCountry: mngMerchant.senderCountry?.trim() || 'Türkiye',
          SenderCity: mngMerchant.senderCity.trim(),
          SenderDistrict: mngMerchant.senderDistrict.trim(),
          SenderNeighborhood: mngMerchant.senderNeighborhood?.trim() || null,
          SenderAddressLine: mngMerchant.senderAddressLine.trim(),
          IsTestMode: mngMerchant.isTestMode
        });
      }

      console.log(`✅ [${integration.platform.toUpperCase()} UPDATE] .NET API'de güncellendi!`, result);

      setSnackbar({
        open: true,
        message: t('editIntegrationModal.messages.integrationUpdated', { platform: getPlatformName(integration.platform) }),
        severity: 'success'
      });
      
      setTimeout(() => {
        onSaved();
        handleClose();
      }, 1000);
    } catch (error) {
      console.error('Error while updating integration:', error);
      // HTTP 4xx'te axios fırlatır; backend'in gerekçesi (örn. "Bu mağaza kimliği
      // zaten kullanılıyor") response gövdesinde kalır. Okumazsak kullanıcı yalnızca
      // "Request failed with status code 400" görür ve sorunu teşhis edemez.
      const apiMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.Message;
      setError(
        apiMessage
        || (error instanceof Error ? error.message : t('editIntegrationModal.errors.updateFailed'))
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError(null);
      setSnackbar(prev => ({ ...prev, open: false }));
      onClose();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const testConnection = async () => {
    if (!companyId) {
      setError(t('editIntegrationModal.errors.companyInfoNotFound'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Credentials validasyonu
      if (isFoodPlatform) {
        if (!formData.credentials.restaurantId?.trim()) {
          throw new Error(t('editIntegrationModal.errors.restaurantIdNotFound'));
        }
        if (!formData.credentials.appSecretKey?.trim()) {
          throw new Error(t('editIntegrationModal.errors.appSecretNotFound'));
        }
        if (integration.platform !== 'yemeksepeti' && !formData.credentials.restaurantSecretKey?.trim()) {
          throw new Error(t('editIntegrationModal.errors.restaurantSecretNotFound'));
        }
      } else {
        if (integration.platform !== 'hepsiburada' && !formData.credentials.apiKey?.trim()) {
          throw new Error(t('editIntegrationModal.errors.apiKeyNotFound'));
        }
        if (!formData.credentials.apiSecret?.trim()) {
          throw new Error(t('editIntegrationModal.errors.apiSecretNotFound'));
        }
        if (integration.platform === 'hepsiburada' && !formData.credentials.sellerId?.trim()) {
          throw new Error('Hepsiburada icin MerchantId zorunludur');
        }
      }
      
      console.log(`✅ [${integration.platform.toUpperCase()} TEST] Credentials kontrolü başarılı`);

      // Gerçek bağlantı testi olan pazaryerleri kendi API'sine yönlendirilir;
      // amazon (backend yok) genel credential doğrulamasına düşer.
      const marketplaceTestApi =
        integration.platform === 'trendyol' ? trendyolMpApi :
        integration.platform === 'n11' ? n11Api :
        integration.platform === 'hepsiburada' ? hepsiburadaApi :
        integration.platform === 'pazarama' ? pazaramaApi :
        integration.platform === 'pttavm' ? pttavmApi : null;

      // Test sonucunu PlatformIntegration kaydina yazar; "Mevcut Durum" rozeti buna gore yesil/kirmizi olur.
      const persistStatus = async (connected: boolean, errorMessage: string | null) => {
        try {
          await mainApi.post('/api/platformintegration/update-connection', {
            id: integration.id,
            isConnected: connected,
            errorMessage
          });
          setConnectedOverride(connected);
          onSaved();
        } catch (persistErr) {
          console.error('Connection status persist error:', persistErr);
        }
      };

      const runGenericValidation = async () => {
        const response = await mainApi.post('/api/platformintegration/update-connection', {
          id: integration.id,
          isConnected: true,
          errorMessage: null
        });
        const result = response.data;
        if (result.success) {
          setConnectedOverride(true);
          onSaved();
          setSnackbar({
            open: true,
            message: t('editIntegrationModal.messages.credentialsValidated', { platform: getPlatformName(integration.platform) }),
            severity: 'success'
          });
        } else {
          throw new Error(result.message || t('editIntegrationModal.errors.connectionTestFailed'));
        }
      };

      if (marketplaceTestApi) {
        let endpointMissing = false;
        let apiResult: any = null;
        try {
          apiResult = await marketplaceTestApi.post(`/api/${integration.platform}/integration/test`);
        } catch (apiErr: any) {
          // Test endpoint'i henüz deploy edilmemişse (404) eski genel doğrulamaya düş.
          if (apiErr?.response?.status === 404) {
            endpointMissing = true;
          } else {
            const msg = apiErr?.response?.data?.Message || apiErr?.response?.data?.message || (apiErr instanceof Error ? apiErr.message : null);
            await persistStatus(false, msg ?? null);
            throw apiErr;
          }
        }

        if (endpointMissing) {
          await runGenericValidation();
        } else {
          const payload = apiResult.data;
          const success = payload?.Success ?? payload?.success;
          if (success) {
            await persistStatus(true, null);
            setSnackbar({
              open: true,
              message: t('editIntegrationModal.messages.credentialsValidated', { platform: getPlatformName(integration.platform) }),
              severity: 'success'
            });
          } else {
            const msg = payload?.Message || payload?.message || t('editIntegrationModal.errors.connectionTestFailed');
            await persistStatus(false, msg);
            throw new Error(msg);
          }
        }
      } else {
        await runGenericValidation();
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setError(error instanceof Error ? error.message : t('editIntegrationModal.errors.connectionTestError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="div">
            {t('editIntegrationModal.title')}
          </Typography>
          <Chip 
            label={platformInfo.label}
            sx={{ 
              bgcolor: platformInfo.color,
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Durum Bilgileri */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('editIntegrationModal.currentStatus.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={(connectedOverride ?? integration.status?.connected ?? integration.isConnected) ? t('editIntegrationModal.currentStatus.connected') : t('editIntegrationModal.currentStatus.connectionError')}
              color={(connectedOverride ?? integration.status?.connected ?? integration.isConnected) ? 'success' : 'error'}
              size="small"
            />
            <Chip 
              label={t('editIntegrationModal.currentStatus.errorCount', { count: integration.status?.errorCount ?? integration.errorCount ?? 0 })}
              color={(integration.status?.errorCount ?? integration.errorCount ?? 0) === 0 ? 'success' : 'warning'}
              size="small"
            />
            {(integration.status?.lastSync || integration.lastSyncAt) && (
              <Chip 
                label={t('editIntegrationModal.currentStatus.lastSynchronization', { date: new Date(integration.status?.lastSync || integration.lastSyncAt || '').toLocaleString(currentLocale) })}
                variant="outlined"
                size="small"
              />
            )}
            {integration.branchId && (
              <Chip 
                label={t('editIntegrationModal.currentStatus.branch', { branch: getBranchName(integration.branchId) })}
                variant="outlined"
                color="info"
                size="small"
              />
            )}
          </Box>
        </Box>

        {/* Getir POS Durumu */}
        {integration.platform === 'getir' && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: getirPosStatus === 'open' ? 'success.50' : getirPosStatus === 'closed' ? 'error.50' : 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: getirPosStatus === 'open' ? 'success.200' : getirPosStatus === 'closed' ? 'error.200' : 'grey.200'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('editIntegrationModal.getirPos.title')}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={checkGetirPosStatus}
                disabled={getirPosStatus === 'loading'}
                startIcon={getirPosStatus === 'loading' ? <CircularProgress size={14} /> : undefined}
              >
                {t('editIntegrationModal.actions.refresh')}
              </Button>
            </Box>
            
            {getirPosStatus === 'loading' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">{t('editIntegrationModal.getirPos.checkingStatus')}</Typography>
              </Box>
            )}
            
            {getirPosStatus === 'error' && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('editIntegrationModal.getirPos.fetchStatusError', { message: getirPosError })}
              </Alert>
            )}
            
            {(getirPosStatus === 'open' || getirPosStatus === 'closed') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={getirPosStatus === 'open' ? t('editIntegrationModal.getirPos.open') : t('editIntegrationModal.getirPos.closed')}
                  color={getirPosStatus === 'open' ? 'success' : 'error'}
                  size="medium"
                  sx={{ fontWeight: 'bold', fontSize: '0.9rem', px: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  color={getirPosStatus === 'open' ? 'error' : 'success'}
                  onClick={() => handleGetirPosToggle(getirPosStatus === 'open' ? 'close' : 'open')}
                  disabled={getirPosLoading}
                  startIcon={getirPosLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                  sx={{ minWidth: 140 }}
                >
                  {getirPosLoading 
                    ? (getirPosStatus === 'open' ? t('editIntegrationModal.getirPos.closing') : t('editIntegrationModal.getirPos.opening'))
                    : (getirPosStatus === 'open' ? t('editIntegrationModal.getirPos.closePosIntegration') : t('editIntegrationModal.getirPos.openPosIntegration'))
                  }
                </Button>
              </Box>
            )}
            
            {getirPosError && getirPosStatus !== 'error' && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {getirPosError}
              </Alert>
            )}
          </Box>
        )}

        {/* Genel Ayarlar */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('addIntegrationModal.form.generalSettings')}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
              />
            }
            label={t('addIntegrationModal.form.enableIntegration')}
          />
          
          {/* Yemek entegrasyonları için şube seçici */}
          {isFoodPlatform && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="branch-select-label">{t('editIntegrationModal.form.branch')}</InputLabel>
              <Select
                labelId="branch-select-label"
                value={formData.branchId || ''}
                label={t('editIntegrationModal.form.branch')}
                onChange={(e) => handleInputChange('branchId', e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('addIntegrationModal.form.selectBranch')}</em>
                </MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Mağaza / Marka adı */}
          {isFoodPlatform && (
            <TextField
              fullWidth
              label={t('addIntegrationModal.form.storeName')}
              value={formData.storeName}
              onChange={(e) => handleInputChange('storeName', e.target.value)}
              margin="normal"
              required
              inputProps={{ maxLength: STORE_NAME_MAX_LENGTH }}
              helperText={t('addIntegrationModal.form.storeNameHelper')}
            />
          )}
        </Box>

        {/* Markaya özel fiş başlığı */}
        {isFoodPlatform && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setReceiptSectionOpen(prev => !prev)}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('editIntegrationModal.brandReceipt.title')}
                </Typography>
                <IconButton size="small">
                  {receiptSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              {!brandReceipt && (
                <Typography variant="caption" color="text.secondary">
                  {t('editIntegrationModal.brandReceipt.usingScreenDefault')}
                </Typography>
              )}

              <Collapse in={receiptSectionOpen} unmountOnExit>
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('editIntegrationModal.brandReceipt.description')}
                  </Alert>

                  <BrandReceiptHeaderForm
                    value={brandReceipt || {}}
                    onChange={(next) => setBrandReceipt(next)}
                    onImageUpload={uploadBrandLogo}
                    disabled={saving}
                  />

                  {brandReceipt && (
                    <Button
                      size="small"
                      color="inherit"
                      sx={{ mt: 2 }}
                      onClick={() => {
                        setBrandReceipt(null);
                        setReceiptSectionOpen(false);
                      }}
                    >
                      {t('editIntegrationModal.brandReceipt.resetToDefault')}
                    </Button>
                  )}
                </Box>
              </Collapse>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* API Bilgileri */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('addIntegrationModal.form.apiInformation')}
          </Typography>
          
          {!isFoodPlatform && (
            <>
              {integration.platform === 'hepsiburada' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Hepsiburada icin yalnizca Secret Key ve MerchantId gereklidir.
                </Alert>
              )}

              {integration.platform === 'hepsijet' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('addIntegrationModal.form.hepsijetCredentialHint')}
                </Alert>
              )}

              {integration.platform === 'pazarama' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Pazarama icin Client ID, Client Secret ve Satici ID gereklidir
                  (isortagim.pazarama.com - Hesap Bilgileri).
                </Alert>
              )}

              {integration.platform === 'pttavm' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  PttAVM icin Api-Key ve Access-Token gereklidir.
                </Alert>
              )}

              {integration.platform === 'mng' && (
                <>
                  <FormControlLabel
                    sx={{ mt: 1, display: 'block' }}
                    control={
                      <Switch
                        checked={mngMerchant.isTestMode}
                        onChange={(e) => handleMngMerchantChange('isTestMode', e.target.checked)}
                      />
                    }
                    label={t('addIntegrationModal.form.mngTestMode')}
                  />

                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold' }}>
                    {t('addIntegrationModal.form.mngApizoneSectionTitle')}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t('addIntegrationModal.form.mngApizoneHint')}
                  </Alert>
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngClientId')}
                    value={mngPlatform.clientId}
                    onChange={(e) => handleMngPlatformChange('clientId', e.target.value)}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.mngClientIdHelper')}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngClientSecret')}
                    type={showMngClientSecret ? 'text' : 'password'}
                    value={mngPlatform.clientSecret}
                    onChange={(e) => handleMngPlatformChange('clientSecret', e.target.value)}
                    margin="normal"
                    required={!mngPlatform.hasClientSecret}
                    helperText={mngPlatform.hasClientSecret
                      ? t('addIntegrationModal.form.mngClientSecretKeepHelper')
                      : t('addIntegrationModal.form.mngClientSecretHelper')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowMngClientSecret(v => !v)} edge="end">
                            {showMngClientSecret ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngApiBaseUrl')}
                    value={mngPlatform.apiBaseUrl}
                    margin="normal"
                    InputProps={{ readOnly: true }}
                    helperText={t('addIntegrationModal.form.mngApiBaseUrlHelper')}
                  />

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    {t('addIntegrationModal.form.mngMerchantCredentialTitle')}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t('addIntegrationModal.form.mngCredentialHint')}
                  </Alert>
                </>
              )}

              {integration.platform !== 'hepsiburada' && (
                <TextField
                  fullWidth
                  label={integration.platform === 'mng'
                    ? t('addIntegrationModal.form.mngCustomerNumber')
                    : isCargoPlatform
                    ? t('addIntegrationModal.form.username')
                    : integration.platform === 'pazarama'
                    ? 'Client ID'
                    : integration.platform === 'pttavm'
                    ? 'Api-Key'
                    : t('addIntegrationModal.form.apiKey')}
                  value={formData.credentials.apiKey}
                  onChange={(e) => handleInputChange('credentials', { apiKey: e.target.value })}
                  margin="normal"
                  required
                  helperText={integration.platform === 'mng'
                    ? t('addIntegrationModal.form.mngCustomerNumberHelper')
                    : isCargoPlatform
                    ? t('addIntegrationModal.form.usernameHelper')
                    : integration.platform === 'pazarama'
                    ? 'Pazarama Hesap Bilgileri ekranindaki clientId degeri.'
                    : `API key from your ${platformInfo.label} panel`}
                />
              )}
              
              <TextField
                fullWidth
                label={integration.platform === 'mng'
                  ? t('addIntegrationModal.form.mngPassword')
                  : isCargoPlatform
                  ? t('addIntegrationModal.form.password')
                  : integration.platform === 'pazarama'
                  ? 'Client Secret'
                  : integration.platform === 'pttavm'
                  ? 'Access-Token'
                  : t('addIntegrationModal.form.apiSecret')}
                type={showApiSecret ? 'text' : 'password'}
                value={formData.credentials.apiSecret}
                onChange={(e) => handleInputChange('credentials', { apiSecret: e.target.value })}
                margin="normal"
                required
                helperText={integration.platform === 'hepsiburada'
                  ? 'Hepsiburada panelindeki Servis Anahtari (SecretKey) degerini girin.'
                  : integration.platform === 'pazarama'
                  ? 'Pazarama Hesap Bilgileri ekranindaki clientSecret degeri.'
                  : integration.platform === 'mng'
                  ? t('addIntegrationModal.form.mngPasswordHelper')
                  : isCargoPlatform
                  ? t('addIntegrationModal.form.passwordHelper')
                  : `Secret key from your ${platformInfo.label} panel`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={toggleShowApiSecret}
                        edge="end"
                      >
                        {showApiSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {integration.platform === 'hepsijet' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    {t('addIntegrationModal.form.merchantSectionTitle')}
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {t('addIntegrationModal.form.merchantSectionHint')}
                  </Alert>

                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantCompanyName')}
                    value={hepsijetMerchant.companyName}
                    onChange={(e) => handleMerchantChange('companyName', e.target.value)}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.merchantCompanyNameHelper')}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantAbbreviationCode')}
                    value={hepsijetMerchant.abbreviationCode}
                    onChange={(e) => handleMerchantChange('abbreviationCode', e.target.value)}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.merchantAbbreviationCodeHelper')}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantCompanyAddressId')}
                    value={hepsijetMerchant.companyAddressId}
                    onChange={(e) => handleMerchantChange('companyAddressId', e.target.value)}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.merchantCompanyAddressIdHelper')}
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantXDockCode')}
                    value={hepsijetMerchant.currentXDockCode}
                    onChange={(e) => handleMerchantChange('currentXDockCode', e.target.value)}
                    margin="normal"
                    required
                    helperText={t('addIntegrationModal.form.merchantXDockCodeHelper')}
                  />

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 'bold' }}>
                    {t('addIntegrationModal.form.merchantSenderAddressTitle')}
                  </Typography>
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantSenderCity')}
                    value={hepsijetMerchant.senderCity}
                    onChange={(e) => handleMerchantChange('senderCity', e.target.value)}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantSenderTown')}
                    value={hepsijetMerchant.senderTown}
                    onChange={(e) => handleMerchantChange('senderTown', e.target.value)}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantSenderDistrict')}
                    value={hepsijetMerchant.senderDistrict}
                    onChange={(e) => handleMerchantChange('senderDistrict', e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.merchantSenderAddress')}
                    value={hepsijetMerchant.senderAddressLine1}
                    onChange={(e) => handleMerchantChange('senderAddressLine1', e.target.value)}
                    margin="normal"
                    required
                    multiline
                    minRows={2}
                  />
                </>
              )}

              {integration.platform === 'mng' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    {t('addIntegrationModal.form.mngMerchantSectionTitle')}
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {t('addIntegrationModal.form.mngMerchantSectionHint')}
                  </Alert>

                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngSenderName')}
                    value={mngMerchant.senderName}
                    onChange={(e) => handleMngMerchantChange('senderName', e.target.value)}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngSenderPhone')}
                    value={mngMerchant.senderPhone}
                    onChange={(e) => handleMngMerchantChange('senderPhone', e.target.value)}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngSenderCity')}
                    value={mngMerchant.senderCity}
                    onChange={(e) => handleMngMerchantChange('senderCity', e.target.value)}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngSenderDistrict')}
                    value={mngMerchant.senderDistrict}
                    onChange={(e) => handleMngMerchantChange('senderDistrict', e.target.value)}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngSenderNeighborhood')}
                    value={mngMerchant.senderNeighborhood}
                    onChange={(e) => handleMngMerchantChange('senderNeighborhood', e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label={t('addIntegrationModal.form.mngSenderAddress')}
                    value={mngMerchant.senderAddressLine}
                    onChange={(e) => handleMngMerchantChange('senderAddressLine', e.target.value)}
                    margin="normal"
                    required
                    multiline
                    minRows={2}
                  />
                </>
              )}
            </>
          )}
          
          {['trendyol', 'hepsiburada', 'amazon', 'pazarama'].includes(integration.platform) && (
            <TextField
              fullWidth
              label={integration.platform === 'hepsiburada'
                ? 'MerchantId (Magaza ID)'
                : integration.platform === 'pazarama'
                ? 'Satici ID (SellerId)'
                : t('addIntegrationModal.form.sellerId')}
              value={formData.credentials.sellerId}
              onChange={(e) => handleInputChange('credentials', { sellerId: e.target.value })}
              margin="normal"
              required
              helperText={integration.platform === 'hepsiburada'
                ? 'Hepsiburada magaza MerchantId degerini girin.'
                : integration.platform === 'pazarama'
                ? 'Pazarama satici (seller) ID degeri. Siparis senkronu icin kullanilir.'
                : `Seller identifier for your ${platformInfo.label} account`}
            />
          )}

          {isFoodPlatform && integration.platform === 'trendyol_yemek' && (
            <>
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.supplierId')}
                value={formData.credentials.sellerId}
                onChange={(e) => handleInputChange('credentials', { sellerId: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.supplierIdHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.storeId')}
                value={formData.credentials.restaurantId}
                onChange={(e) => handleInputChange('credentials', { restaurantId: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.storeIdHelper')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.apiKey')}
                value={formData.credentials.apiKey}
                onChange={(e) => handleInputChange('credentials', { apiKey: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.apiKeyHelperTrendyolFood')}
              />
              <TextField
                fullWidth
                label={t('addIntegrationModal.form.apiSecret')}
                type={showApiSecret ? 'text' : 'password'}
                value={formData.credentials.apiSecret}
                onChange={(e) => handleInputChange('credentials', { apiSecret: e.target.value })}
                margin="normal"
                required
                helperText={t('addIntegrationModal.form.apiSecretHelperTrendyolFood')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleShowApiSecret} edge="end">
                        {showApiSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}

          {isFoodPlatform && integration.platform !== 'trendyol_yemek' && (
            <>
              <TextField
                fullWidth
                label={integration.platform === 'migros_yemek' ? 'Restaurant ID (Store ID)' : integration.platform === 'yemeksepeti' ? 'Remote ID' : 'Restaurant ID'}
                value={formData.credentials.restaurantId}
                onChange={(e) => handleInputChange('credentials', { restaurantId: e.target.value })}
                margin="normal"
                required
                helperText={integration.platform === 'migros_yemek'
                  ? 'Your Migros Food store identifier (StoreId)'
                  : integration.platform === 'yemeksepeti'
                  ? t('addIntegrationModal.form.remoteIdHelper')
                  : `Restaurant identifier for your ${platformInfo.label} account`}
              />
              <TextField
                fullWidth
                label={integration.platform === 'migros_yemek' ? 'XApiKey (Restaurant API Key)' : integration.platform === 'yemeksepeti' ? 'Chain Code' : 'App Secret Key'}
                type={showApiSecret ? 'text' : 'password'}
                value={formData.credentials.appSecretKey}
                onChange={(e) => handleInputChange('credentials', { appSecretKey: e.target.value })}
                margin="normal"
                required
                helperText={integration.platform === 'migros_yemek'
                  ? 'API key provided specifically for your restaurant by Migros Food (XApiKey)'
                  : integration.platform === 'yemeksepeti'
                  ? t('addIntegrationModal.form.chainCodeHelper')
                  : `Application secret key from your ${platformInfo.label} panel`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleShowApiSecret} edge="end">
                        {showApiSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {integration.platform !== 'yemeksepeti' && (
              <TextField
                fullWidth
                label={integration.platform === 'migros_yemek' ? 'Chain ID (Store Group ID)' : 'Restaurant Secret Key'}
                type={integration.platform === 'migros_yemek' ? 'text' : (showApiSecret ? 'text' : 'password')}
                value={formData.credentials.restaurantSecretKey}
                onChange={(e) => handleInputChange('credentials', { restaurantSecretKey: e.target.value })}
                margin="normal"
                required
                helperText={integration.platform === 'migros_yemek'
                  ? 'Your Migros Food chain identifier (StoreGroupId)'
                  : `Restaurant secret key from your ${platformInfo.label} panel`}
                InputProps={integration.platform !== 'migros_yemek' ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleShowApiSecret} edge="end">
                        {showApiSecret ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                } : undefined}
              />
              )}
            </>
          )}

          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={testConnection}
              disabled={saving}
              size="small"
              sx={{
                borderColor: integration.platform === 'trendyol' ? '#f27a1a' : undefined,
                color: integration.platform === 'trendyol' ? '#f27a1a' : undefined,
                '&:hover': {
                  borderColor: integration.platform === 'trendyol' ? '#f27a1a' : undefined,
                  backgroundColor: integration.platform === 'trendyol' ? 'rgba(242, 122, 26, 0.04)' : undefined
                }
              }}
            >
              {saving ? <CircularProgress size={20} /> : 
                integration.platform === 'trendyol' ? t('editIntegrationModal.actions.testTrendyolApi') : t('editIntegrationModal.actions.testConnection')
              }
            </Button>
            {integration.platform === 'trendyol' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('editIntegrationModal.testSequence')}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>



        {/* Son Güncelleme Bilgisi */}
        {(integration.credentials?.lastUpdated || integration.updatedAt) && (
          <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="caption" color="info.contrastText">
              {t('editIntegrationModal.lastUpdate', { date: new Date(integration.credentials?.lastUpdated || integration.updatedAt || '').toLocaleString(currentLocale) })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          {t('addIntegrationModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving}
          sx={{ 
            bgcolor: platformInfo.color,
            '&:hover': { 
              bgcolor: platformInfo.color,
              opacity: 0.8
            }
          }}
        >
          {saving ? <CircularProgress size={24} /> : t('editIntegrationModal.actions.update')}
        </Button>
      </DialogActions>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditIntegrationModal;
